import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { DocumentLibraryService } from '@dealstack/frontend/shared/dataExchange/services/document-library/document-library.service';
import { BulkUpdateDocumentClassifierAction } from '@dealstack/shared/lib/generated';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { buildDocumentsWithFilteringQueryKey } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/hooks/useDocumentsWithFiltering';
import {
	getClassificationGroup,
	getClassificationId,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/DashboardMain/utils';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { TreeItemsWithMeta } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/utils';

export type BulkUpdateActionType =
	| BulkUpdateDocumentClassifierAction.ADD
	| BulkUpdateDocumentClassifierAction.REMOVE
	| 'none';

interface UseBulkUpdateDocumentClassifiersParams {
	dataOperations: TableOperations<{
		relatedToIds: string[];
		updatedAt: { from: string; to: string };
	}> | null;
	dealClassifierIdsToUpdate: string[];
	selectedDocumentIds: string[];
	dealId: string;
	updateAction: BulkUpdateActionType;
	bulkUpdateUsingFilters: boolean;
	selectedTreeItem: TreeItemsWithMeta | undefined;
	onSuccess?: (data: unknown) => void;
	onError?: (err: unknown) => void;
}

export const useBulkUpdateDocumentClassifiers = ({
	dataOperations,
	dealClassifierIdsToUpdate,
	selectedDocumentIds,
	dealId,
	updateAction,
	bulkUpdateUsingFilters = false,
	selectedTreeItem,
	onSuccess,
	onError,
}: UseBulkUpdateDocumentClassifiersParams) => {
	const mutationEndpoint = formatUnicorn(
		API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENT_CLASSIFIERS_PUT,
		{
			dealId,
		}
	);

	const queryClient = useQueryClient();

	const classificationId = getClassificationId(selectedTreeItem);
	const classificationGroup = getClassificationGroup(selectedTreeItem);
	const mutationKey = buildDocumentsWithFilteringQueryKey(mutationEndpoint, {
		dealId,
		dataOperations,
		classificationId,
		classificationGroup,
	});

	const mutationFn = async () => {
		if (updateAction === 'none') {
			showToastV2({
				title: 'No action selected',
				message: 'Please select an action to apply to the classifiers.',
				state: 'error',
			});
			return;
		}

		return DocumentLibraryService.bulkUpdateDocumentClassifiers({
			endpoint: mutationKey[0],
			filters: mutationKey[1],
			body: {
				documentIdsToUpdate: selectedDocumentIds,
				dealClassifierIdsToUpdate,
				action: updateAction,
				bulkUpdateUsingFilters,
			},
		});
	};

	const { mutateAsync: runBulkUpdate, isPending } = useMutation({
		mutationFn,

		onSuccess: async (data) => {
			const documentsEndpoint = formatUnicorn(
				API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENTS_GET,
				{ dealId }
			);

			const queryKey = buildDocumentsWithFilteringQueryKey(documentsEndpoint, {
				dealId,
				dataOperations,
				classificationId,
				classificationGroup,
			});

			await queryClient.refetchQueries({
				queryKey,
			});
			onSuccess?.(data);
		},

		onError,
	});

	return { runBulkUpdate, isPending };
};
